/**
 * Event tracking — "הגדרת משפכים" tab. Funnels and their stages.
 *
 * A funnel = ordered stages; each stage matches every event whose type STARTS WITH its event type
 * (page_view also matches page_view_home). Optional stages are shown but don't affect conversion %.
 * One funnel can be "main" (shown full width on the dashboard).
 *
 * Layout: funnel list (side) · stages of the selected funnel (table: order ▲▼, name, event type,
 * חובה / פעיל switches, edit / delete). Funnel and stage forms open in side drawers.
 *
 * Data (unchanged): getAllFunnels(false) · create/update/deleteFunnel · seedDefaultFunnel ·
 * getFunnelStages(id, false) · create/update/deleteFunnelStage · reorderFunnelStages · event-type
 * suggestions from GET /api/funnel-stages/event-types. Same payloads and messages; the
 * window.confirm() deletes are now the app's confirm dialog with the same text.
 */
import React, { useState, useEffect } from 'react';
import { PlusOutlined, EditOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import {
  Section,
  DataTable,
  RowActions,
  EmptyState,
  EditDrawer,
  FormGrid,
  Field,
  Button,
  Input,
  Switch,
  Segmented,
  Tooltip,
  AutoComplete,
  Spin,
  useBreakpoint,
} from '../../../ui';
import { confirmAction } from '../../../ui/feedback';
import classes from './FunnelStageManager.css';

const FunnelStageManager = (props) => {
  const [funnels, setFunnels] = useState([]);
  const [selectedFunnel, setSelectedFunnel] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFunnelForm, setShowFunnelForm] = useState(false);
  const [editingFunnel, setEditingFunnel] = useState(null);
  const [showStageForm, setShowStageForm] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [reordering, setReordering] = useState(false); // UI only

  // Funnel form state
  const [funnelName, setFunnelName] = useState('');
  const [funnelDescription, setFunnelDescription] = useState('');
  const [funnelIsActive, setFunnelIsActive] = useState(true);
  const [funnelIsMain, setFunnelIsMain] = useState(false);

  // Stage form state
  const [stageName, setStageName] = useState('');
  const [stageEventType, setStageEventType] = useState('');
  const [stageIsActive, setStageIsActive] = useState(true);
  const [stageIsMandatory, setStageIsMandatory] = useState(true);
  const [eventTypeSuggestions, setEventTypeSuggestions] = useState([]);

  // UI only: what the open form started with (for the "close without saving?" guard)
  const [funnelInitial, setFunnelInitial] = useState('');
  const [stageInitial, setStageInitial] = useState('');
  const screens = useBreakpoint();

  useEffect(() => {
    loadFunnels();
    loadEventTypeSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEventTypeSuggestions = async () => {
    try {
      const res = await fetch('/api/funnel-stages/event-types');
      const data = await res.json();
      if (Array.isArray(data)) setEventTypeSuggestions(data.filter(Boolean));
    } catch (e) {
      console.error('Error loading event type suggestions:', e);
    }
  };

  useEffect(() => {
    if (selectedFunnel) {
      loadStages(selectedFunnel.id);
    } else {
      setStages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFunnel]);

  const loadFunnels = async () => {
    setLoading(true);
    try {
      const data = await props.getAllFunnels(false);
      setFunnels(data.funnels || []);
      // Auto-select first funnel if none selected
      if (data.funnels && data.funnels.length > 0 && !selectedFunnel) {
        setSelectedFunnel(data.funnels[0]);
      } else if (selectedFunnel) {
        // keep the selection but show its fresh name / badges
        const fresh = (data.funnels || []).find((f) => f.id === selectedFunnel.id);
        if (fresh) setSelectedFunnel(fresh);
      }
    } catch (error) {
      console.error('Error loading funnels:', error);
      props.showAlert('שגיאה בטעינת משפכים');
    }
    setLoading(false);
  };

  const loadStages = async (funnelId) => {
    try {
      const data = await props.getFunnelStages(funnelId, false);
      setStages(data.stages || []);
    } catch (error) {
      console.error('Error loading stages:', error);
      props.showAlert('שגיאה בטעינת שלבי המשפך');
    }
  };

  const handleSeedFunnel = async () => {
    try {
      await props.seedDefaultFunnel();
      props.showAlert('משפך ברירת מחדל נוצר בהצלחה');
      loadFunnels();
    } catch (error) {
      console.error('Error seeding funnel:', error);
      props.showAlert('שגיאה ביצירת משפך ברירת מחדל');
    }
  };

  // ============= FUNNEL OPERATIONS =============

  const funnelSnapshot = (name, description, isActive, isMain) => JSON.stringify([name, description, isActive, isMain]);

  const resetFunnelForm = () => {
    setFunnelName('');
    setFunnelDescription('');
    setFunnelIsActive(true);
    setFunnelIsMain(false);
    setEditingFunnel(null);
    setShowFunnelForm(false);
  };

  const handleAddFunnel = () => {
    resetFunnelForm();
    setFunnelInitial(funnelSnapshot('', '', true, false));
    setShowFunnelForm(true);
  };

  const handleEditFunnel = (funnel) => {
    setEditingFunnel(funnel);
    setFunnelName(funnel.name);
    setFunnelDescription(funnel.description || '');
    setFunnelIsActive(funnel.is_active);
    setFunnelIsMain(funnel.is_main);
    setFunnelInitial(funnelSnapshot(funnel.name, funnel.description || '', funnel.is_active, funnel.is_main));
    setShowFunnelForm(true);
  };

  const handleSaveFunnel = async () => {
    if (!funnelName) {
      props.showAlert('יש למלא שם משפך');
      return;
    }
    try {
      if (editingFunnel) {
        await props.updateFunnel(editingFunnel.id, {
          name: funnelName,
          description: funnelDescription,
          isActive: funnelIsActive,
          isMain: funnelIsMain,
        });
        props.showAlert('משפך עודכן בהצלחה');
      } else {
        await props.createFunnel({
          name: funnelName,
          description: funnelDescription,
          isActive: funnelIsActive,
          isMain: funnelIsMain,
        });
        props.showAlert('משפך נוצר בהצלחה');
      }
      resetFunnelForm();
      loadFunnels();
    } catch (error) {
      console.error('Error saving funnel:', error);
      props.showAlert('שגיאה בשמירת משפך: ' + ((error.response && error.response.data && error.response.data.error) || error.message));
    }
  };

  const handleDeleteFunnel = (funnelId) => {
    confirmAction({
      text: 'האם אתה בטוח שברצונך למחוק משפך זה וכל השלבים שלו?',
      onYes: async () => {
        try {
          await props.deleteFunnel(funnelId);
          props.showAlert('משפך נמחק בהצלחה');
          if (selectedFunnel && selectedFunnel.id === funnelId) {
            setSelectedFunnel(null);
          }
          resetFunnelForm();
          loadFunnels();
        } catch (error) {
          console.error('Error deleting funnel:', error);
          props.showAlert('שגיאה במחיקת משפך');
        }
      },
    });
  };

  // ============= STAGE OPERATIONS =============

  const stageSnapshot = (name, eventType, isActive, isMandatory) => JSON.stringify([name, eventType, isActive, isMandatory]);

  const resetStageForm = () => {
    setStageName('');
    setStageEventType('');
    setStageIsActive(true);
    setStageIsMandatory(true);
    setEditingStage(null);
    setShowStageForm(false);
  };

  const handleAddStage = () => {
    if (!selectedFunnel) {
      props.showAlert('יש לבחור משפך תחילה');
      return;
    }
    resetStageForm();
    setStageInitial(stageSnapshot('', '', true, true));
    setShowStageForm(true);
  };

  const handleEditStage = (stage) => {
    setEditingStage(stage);
    setStageName(stage.name);
    setStageEventType(stage.event_type);
    setStageIsActive(stage.is_active);
    setStageIsMandatory(stage.is_mandatory !== false);
    setStageInitial(stageSnapshot(stage.name, stage.event_type, stage.is_active, stage.is_mandatory !== false));
    setShowStageForm(true);
  };

  const handleSaveStage = async () => {
    if (!stageName || !stageEventType) {
      props.showAlert('יש למלא שם וסוג אירוע');
      return;
    }
    try {
      if (editingStage) {
        await props.updateFunnelStage(editingStage.id, {
          name: stageName,
          eventType: stageEventType,
          isActive: stageIsActive,
          isMandatory: stageIsMandatory,
        });
        props.showAlert('שלב עודכן בהצלחה');
      } else {
        await props.createFunnelStage(selectedFunnel.id, {
          name: stageName,
          eventType: stageEventType,
          isActive: stageIsActive,
          isMandatory: stageIsMandatory,
        });
        props.showAlert('שלב נוסף בהצלחה');
      }
      resetStageForm();
      loadStages(selectedFunnel.id);
    } catch (error) {
      console.error('Error saving stage:', error);
      props.showAlert('שגיאה בשמירת שלב: ' + ((error.response && error.response.data && error.response.data.error) || error.message));
    }
  };

  const handleDeleteStage = (stageId) => {
    confirmAction({
      text: 'האם אתה בטוח שברצונך למחוק שלב זה?',
      onYes: async () => {
        try {
          await props.deleteFunnelStage(stageId);
          props.showAlert('שלב נמחק בהצלחה');
          loadStages(selectedFunnel.id);
        } catch (error) {
          console.error('Error deleting stage:', error);
          props.showAlert('שגיאה במחיקת שלב');
        }
      },
    });
  };

  const handleToggleStageActive = async (stage) => {
    try {
      await props.updateFunnelStage(stage.id, { isActive: !stage.is_active });
      loadStages(selectedFunnel.id);
    } catch (error) {
      console.error('Error toggling stage:', error);
      props.showAlert('שגיאה בעדכון סטטוס שלב');
    }
  };

  const handleToggleStageMandatory = async (stage) => {
    try {
      await props.updateFunnelStage(stage.id, { isMandatory: !stage.is_mandatory });
      loadStages(selectedFunnel.id);
    } catch (error) {
      console.error('Error toggling mandatory:', error);
      props.showAlert('שגיאה בעדכון שלב');
    }
  };

  // Swap with the neighbour (up = -1, down = +1) and send the whole new order
  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= stages.length) return;
    const newOrder = stages.map((s) => s.id);
    const temp = newOrder[index];
    newOrder[index] = newOrder[target];
    newOrder[target] = temp;
    setReordering(true);
    try {
      await props.reorderFunnelStages(selectedFunnel.id, newOrder);
      await loadStages(selectedFunnel.id);
    } catch (error) {
      console.error('Error reordering stages:', error);
      props.showAlert('שגיאה בסידור מחדש');
    }
    setReordering(false);
  };

  // ============= RENDER =============

  if (loading && funnels.length === 0) {
    return (
      <div className={classes.loading}>
        <Spin />
      </div>
    );
  }

  const stageColumns = [
    {
      key: 'order',
      title: '#',
      width: 92,
      render: (_, stage, index) => (
        <span className={classes.order}>
          <span className={classes.orderNumber}>{stage.stage_order}</span>
          <span className={classes.orderButtons}>
            <Tooltip title="הזז למעלה">
              <Button size="small" type="text" icon={<ArrowUpOutlined />} aria-label="הזז למעלה" disabled={index === 0 || reordering} onClick={() => move(index, -1)} />
            </Tooltip>
            <Tooltip title="הזז למטה">
              <Button size="small" type="text" icon={<ArrowDownOutlined />} aria-label="הזז למטה" disabled={index === stages.length - 1 || reordering} onClick={() => move(index, 1)} />
            </Tooltip>
          </span>
        </span>
      ),
    },
    {
      key: 'name',
      title: 'שלב',
      render: (_, stage) => (
        <div className={classes.stageCell}>
          <span className={classes.stageName}>{stage.name}</span>
          <code className={classes.eventType} dir="ltr">
            {stage.event_type}*
          </code>
        </div>
      ),
    },
    {
      key: 'mandatory',
      title: (
        <Tooltip title="שלב לא חובה לא ישפיע על אחוזי ההמרה">
          <span className={classes.headHint}>חובה</span>
        </Tooltip>
      ),
      width: 80,
      render: (_, stage) => (
        <Switch size="small" checked={stage.is_mandatory !== false} onChange={() => handleToggleStageMandatory(stage)} aria-label="חובה" />
      ),
    },
    {
      key: 'active',
      title: 'פעיל',
      width: 70,
      render: (_, stage) => <Switch size="small" checked={!!stage.is_active} onChange={() => handleToggleStageActive(stage)} aria-label="פעיל" />,
    },
    {
      key: 'actions',
      title: '',
      align: 'left',
      width: screens.md === false ? 56 : 90,
      render: (_, stage) => (
        <RowActions
          actions={[
            { key: 'edit', icon: <EditOutlined />, label: 'עריכה', onClick: () => handleEditStage(stage) },
            { key: 'delete', icon: <DeleteOutlined />, label: 'מחיקה', danger: true, onClick: () => handleDeleteStage(stage.id) },
          ]}
        />
      ),
    },
  ];

  const funnelDirty = showFunnelForm && funnelSnapshot(funnelName, funnelDescription, funnelIsActive, funnelIsMain) !== funnelInitial;
  const stageDirty = showStageForm && stageSnapshot(stageName, stageEventType, stageIsActive, stageIsMandatory) !== stageInitial;
  const stageQuery = stageEventType.toLowerCase();

  return (
    <div className={classes.layout}>
      {/* ---- funnel list ---- */}
      <Section
        title="משפכים"
        className={classes.listCard}
        padded={false}
        extra={
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={handleAddFunnel}>
            משפך
          </Button>
        }
      >
        {funnels.length === 0 ? (
          <EmptyState
            title="לא נמצאו משפכים"
            action={
              <Button type="primary" onClick={handleSeedFunnel}>
                צור משפך ברירת מחדל
              </Button>
            }
          />
        ) : (
          <ul className={classes.funnelList}>
            {funnels.map((funnel) => {
              const isSelected = selectedFunnel && selectedFunnel.id === funnel.id;
              return (
                <li key={funnel.id}>
                  <button
                    type="button"
                    className={classes.funnelItem}
                    data-selected={isSelected || undefined}
                    data-inactive={!funnel.is_active || undefined}
                    onClick={() => setSelectedFunnel(funnel)}
                  >
                    <span className={classes.funnelName}>{funnel.name}</span>
                    {funnel.is_main ? <span className={classes.badgeMain}>ראשי</span> : null}
                    {!funnel.is_active ? <span className={classes.badgeOff}>מושבת</span> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      {/* ---- stages of the selected funnel ---- */}
      {selectedFunnel ? (
        <Section
          padded={false}
          title={
            <span className={classes.detailTitle}>
              {selectedFunnel.name}
              {selectedFunnel.is_main ? <span className={classes.badgeMain}>ראשי</span> : null}
            </span>
          }
          extra={
            <>
              <Button size="small" icon={<EditOutlined />} onClick={() => handleEditFunnel(selectedFunnel)}>
                ערוך משפך
              </Button>
              <Button size="small" type="primary" icon={<PlusOutlined />} onClick={handleAddStage}>
                הוסף שלב
              </Button>
            </>
          }
        >
          {selectedFunnel.description ? <p className={classes.description}>{selectedFunnel.description}</p> : null}
          <DataTable
            data={stages}
            fit
            pagination={false}
            columns={stageColumns}
            onRowClick={handleEditStage}
            empty={
              <EmptyState
                title="לא הוגדרו שלבים למשפך זה"
                description='לחצו על "הוסף שלב" כדי להתחיל'
                action={
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddStage}>
                    הוסף שלב
                  </Button>
                }
              />
            }
          />
          {stages.length > 0 ? (
            <p className={classes.footnote}>
              כל שלב תופס כל אירוע שמתחיל בסוג שלו (<code dir="ltr">page_view*</code> תופס גם <code dir="ltr">page_view_home</code>). שלב
              שאינו חובה מוצג במשפך אבל לא משפיע על אחוזי ההמרה.
            </p>
          ) : null}
        </Section>
      ) : (
        <Section>
          <EmptyState title="בחרו משפך מהרשימה או צרו משפך חדש" />
        </Section>
      )}

      {/* ---- funnel drawer ---- */}
      <EditDrawer
        open={showFunnelForm}
        title={editingFunnel ? 'עריכת משפך' : 'משפך חדש'}
        width={480}
        dirty={funnelDirty}
        onClose={resetFunnelForm}
        footer={(requestClose) => (
          <div className={classes.drawerFooter}>
            {editingFunnel ? (
              <Button danger icon={<DeleteOutlined />} onClick={() => handleDeleteFunnel(editingFunnel.id)}>
                מחק
              </Button>
            ) : (
              <span />
            )}
            <span className={classes.drawerFooterEnd}>
              <Button onClick={requestClose}>ביטול</Button>
              <Button type="primary" onClick={handleSaveFunnel}>
                {editingFunnel ? 'עדכן' : 'הוסף'}
              </Button>
            </span>
          </div>
        )}
      >
        <Section>
          <FormGrid>
            <Field label="שם המשפך" required full>
              <Input value={funnelName} onChange={(e) => setFunnelName(e.target.value)} placeholder="לדוגמה: משפך מכירות" autoFocus />
            </Field>
            <Field label="תיאור" full>
              <Input value={funnelDescription} onChange={(e) => setFunnelDescription(e.target.value)} placeholder="תיאור קצר" />
            </Field>
          </FormGrid>
        </Section>
        <Section>
          <label className={classes.switchRow}>
            <Switch checked={funnelIsMain} onChange={setFunnelIsMain} />
            <span>
              <span className={classes.switchTitle}>משפך ראשי</span>
              <span className={classes.switchHint}>מוצג בגודל מלא בראש הדאשבורד</span>
            </span>
          </label>
          <label className={classes.switchRow}>
            <Switch checked={funnelIsActive} onChange={setFunnelIsActive} />
            <span>
              <span className={classes.switchTitle}>פעיל</span>
              <span className={classes.switchHint}>משפך מושבת לא מוצג בדאשבורד</span>
            </span>
          </label>
        </Section>
      </EditDrawer>

      {/* ---- stage drawer ---- */}
      <EditDrawer
        open={showStageForm}
        title={editingStage ? 'עריכת שלב' : 'שלב חדש'}
        subtitle={selectedFunnel ? `במשפך: ${selectedFunnel.name}` : null}
        width={520}
        dirty={stageDirty}
        onClose={resetStageForm}
        footer={(requestClose) => (
          <>
            <Button onClick={requestClose}>ביטול</Button>
            <Button type="primary" onClick={handleSaveStage}>
              {editingStage ? 'עדכן שלב' : 'הוסף שלב'}
            </Button>
          </>
        )}
      >
        <Section>
          <FormGrid>
            <Field label="שם השלב" required full>
              <Input value={stageName} onChange={(e) => setStageName(e.target.value)} placeholder="לדוגמה: צפייה בדף" autoFocus />
            </Field>
            <Field label="סוג אירוע (באנגלית)" required full hint="מתאים לכל אירוע שמתחיל בשם זה — page_view יתאים גם ל-page_view_home">
              <AutoComplete
                value={stageEventType}
                onChange={(v) => setStageEventType(v || '')}
                placeholder="page_view"
                className={classes.ltrInput}
                options={eventTypeSuggestions
                  .filter((et) => !stageQuery || et.toLowerCase().indexOf(stageQuery) !== -1)
                  .slice(0, 50)
                  .map((et) => ({ value: et, label: <span dir="ltr">{et}</span> }))}
              />
            </Field>
            <Field label="סוג שלב" full>
              <Segmented
                block
                value={stageIsMandatory ? 'true' : 'false'}
                onChange={(v) => setStageIsMandatory(v === 'true')}
                options={[
                  { value: 'true', label: 'חובה — משפיע על אחוזי המרה' },
                  { value: 'false', label: 'אופציונלי — מידע בלבד' },
                ]}
              />
            </Field>
          </FormGrid>
        </Section>
        <Section>
          <label className={classes.switchRow}>
            <Switch checked={stageIsActive} onChange={setStageIsActive} />
            <span>
              <span className={classes.switchTitle}>פעיל</span>
              <span className={classes.switchHint}>שלב מושבת לא נספר במשפך</span>
            </span>
          </label>
        </Section>
      </EditDrawer>
    </div>
  );
};

export default FunnelStageManager;
